// React-related imports
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

// Contexts and Hooks
import { UserContext } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDynamicStylingContext } from '../../contexts/DynamicStylingContext';
import { GeneralizationContext } from '../../contexts/GeneralizationContext';

// Components
import { Modal } from '../Modal';
import EditUserForm from '../user/EditUserForm';
import { Navbar as SideBarNav } from '@GxT-Team/gxt-components';

// Constants
import { FOOTER_ITEMS, NAVBAR_ITEMS } from '../../constants';

// Helpers
import { updatedNavItem } from '../../helpers/NavigationHelpers';
import { hasPermissionForPath } from '../../helpers/routePermissions';
import ErrorBoundary from '../../pages/ErrorBoundary';

const Navbar = () => {
  const { userObj } = useContext(UserContext);

  const { permissions: userPermissions, logOutHandler, authorizedPages } = useAuth();

  const [showModal, setShowModal] = useState(false);

  const { clientStyling } = useDynamicStylingContext();

  const normalizedClientStyling = useMemo(
    () => ({
      ...clientStyling,
      thumbnail: clientStyling?.thumbnail || clientStyling?.logo,
    }),
    [clientStyling],
  );

  const { isFullScreen, setIsFullScreen } = useContext(GeneralizationContext);

  const isLinkActive = useCallback((path: string) => {
    const regex = new RegExp(`^${path.replace(/\*/g, '.*')}$`);
    return regex.test(window.location.pathname);
  }, []);

  const footerItems = useMemo(() => {
    return updatedNavItem({
      navItems: [...FOOTER_ITEMS],
      text: 'Logout',
      newKey: 'onClickHandler',
      newValue: logOutHandler,
    });
  }, [logOutHandler]);

  const accessibleNavItems = useMemo(() => {
    if (!Array.isArray(authorizedPages) || authorizedPages.length === 0) {
      return NAVBAR_ITEMS;
    }

    return NAVBAR_ITEMS.filter((item) => {
      return item.navigateTo ? hasPermissionForPath(authorizedPages, item.navigateTo) : true;
    });
  }, [authorizedPages]);

  useEffect(() => {
    const handleBackButton = () => {
      setIsFullScreen(false);
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [setIsFullScreen]);

  return (
    <>
      <div className="flex min-h-screen">
        <div className="sticky top-0 h-full">
          {!isFullScreen && (
            <SideBarNav
              key={1}
              contentLinks={accessibleNavItems}
              footerLinks={footerItems}
              setShowAvatarMenu={setShowModal}
              showAvatarMenu={showModal}
              clientStyling={normalizedClientStyling}
              user={userObj}
              isLinkActive={isLinkActive}
            />
          )}
        </div>
        <div
          className={`w-full flex min-h-0 flex-1 flex-col min-w-0 ${
            isFullScreen ? 'h-screen' : 'min-h-screen'
          }`}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
      <Modal
        closeModal={() => setShowModal(false)}
        modalIsOpen={showModal}
        title="User Settings"
        size="sm">
        <EditUserForm
          closeModal={() => setShowModal(false)}
          user={{
            ...userObj,
            roles: userPermissions.map((role) => ({ name: role })),
            id: userObj.userId,
          }}
        />
      </Modal>
    </>
  );
};

export default Navbar;
